import { createContext } from "react";

import { differenceInDays, startOfDay } from "date-fns";
import { isEmpty } from "lodash";
import { makeAutoObservable, runInAction } from "mobx";
import * as yup from "yup";

import client from "@/client";
import { graphql } from "@/gql";
import {
  AllergiesKind,
  Maybe,
  MealPlanAttributes,
  Scalars,
} from "@/gql/graphql";
import FormState from "@/store/FormState";
import { parseDate, parseTimeOfDay } from "@/utils/datetime";
import { toNum } from "@/utils/string";
import { FragmentType } from "@/utils/type";

import Cells from "./Cells";
import { Clipboard } from "./Clipboard";
import Columns from "./Columns";
import FoodExchange from "./FoodExchange";
import Macronutrients from "./Macronutrients";
import Rows from "./Rows";

export default class MealPlanFormStore {
  static Context = createContext<MealPlanFormStore>(
    new MealPlanFormStore("template")
  );

  mode: "template" | "program";

  currentStep = 0;
  id = "";
  programName = "";
  clientAllergies: AllergiesKind[] = [];

  // To make working with html inputs easier, we'll mainly update this as
  // string, and have a getter to return it as number for calculations.
  recommendedCalories = "";

  macronutrients = new Macronutrients(this);
  foodExchange = new FoodExchange(this);

  clipboard = new Clipboard(this);
  columns = new Columns(this);
  rows = new Rows(this);
  cells = new Cells(this);

  formState = new FormState(
    this,
    yup
      .object({
        recommendedCalories: yup
          .number()
          .label("Calories")
          .typeError("${label} is required")
          .required()
          .min(1)
          .max(99999),
      })
      .defined()
  );

  get recommendedCaloriesKcal(): number {
    return +this.recommendedCalories;
  }

  constructor(mode: "template" | "program") {
    makeAutoObservable(this);
    this.mode = mode;
  }

  async loadProgram(id: string): Promise<void> {
    const query = await client.query({
      query: programQuery,
      variables: { id, allergies: this.clientAllergies },
      fetchPolicy: "network-only",
    });
    const program = query.data.program;
    runInAction(() => {
      if (program == null) return;
      const mealPlan = program.mealPlan;

      this.programName = program.name;

      this.loadMealPlan(mealPlan);

      // Meal planner
      const startDate = parseDate(program.startDate);
      const endDate = parseDate(program.endDate);
      const duration = differenceInDays(endDate, startDate) + 1;
      this.columns.setDuration(duration, startDate);
      // If there's no planned meals in the meal plan, don't remove the default
      // planned meals.
      if (!isEmpty(mealPlan?.plannedMeals)) {
        this.rows.clear();
      }
      this.loadPlannedMeals(mealPlan?.plannedMeals ?? []);
    });
  }

  async loadTemplate(id: string): Promise<void> {
    const query = await client.query({
      query: mealPlanTemplateQuery,
      variables: { id, allergies: [] },
      fetchPolicy: "network-only",
    });
    const template = query.data.mealPlanTemplate;
    runInAction(() => {
      if (template == null) return;

      this.loadMealPlan(template);

      // Meal planner
      const startDate = startOfDay(new Date());
      this.columns.setDuration(template.durationDays, startDate);
      // If there's no planned meals for the template, don't remove the default
      // planned meals.
      if (!isEmpty(template?.plannedMeals)) {
        this.rows.clear();
      }
      this.loadPlannedMeals(template.plannedMeals);
    });
  }

  async applyTemplate(id: string): Promise<void> {
    const query = await client.query({
      query: mealPlanTemplateQuery,
      variables: { id, allergies: [] },
      fetchPolicy: "network-only",
    });
    const template = query.data.mealPlanTemplate;
    runInAction(() => {
      if (template == null) return;

      this.loadMealPlan(template);

      // Meal planner
      // If there's no planned meals for the template, don't remove the default
      // planned meals.
      if (!isEmpty(template?.plannedMeals)) {
        this.rows.clear();
      }
      template.plannedMeals.forEach((pm) => {
        const plannedMeal = this.rows.add(
          pm.meal,
          parseTimeOfDay(pm.recommendedTime)
        );
        pm.mealPatternItems.forEach((mpi) => {
          const column = this.columns.at(Columns.MEAL_PATTERN_COLUMN_INDEX);
          const cell = this.cells.get(column, plannedMeal);
          cell.addMealPatternItem(
            mpi.name,
            mpi.amountServings,
            mpi.food,
            mpi.foodPortion
          );
        });
        pm.items.forEach((pmi) => {
          const dayColumn = this.columns.at(pmi.day);
          const cell = this.cells.get(dayColumn, plannedMeal);
          cell.addPlannedMealItem(pmi.food, pmi.foodPortion, pmi.amount);
        });
      });
    });
  }

  setClientAllergies(allergies: AllergiesKind[]): void {
    this.clientAllergies = allergies;
  }

  setRecommendedCalories(value: string): void {
    this.recommendedCalories = value;
  }

  setCurrentStep(index: number): void {
    this.currentStep = index;
  }

  previousStep(): void {
    this.currentStep--;
  }

  nextStep(): void {
    this.currentStep++;
  }

  toAttributes(): MealPlanAttributes {
    return {
      recommendedCalories: toNum(this.recommendedCalories),
      ...this.macronutrients.toAttributes(),
      ...this.foodExchange.toAttributes(),
      syncPlannedMealsAttributes: this.rows.toAttributes(),
    };
  }

  private loadMealPlan(mealPlan?: MealPlanLike | null) {
    // Assessment
    this.recommendedCalories = mealPlan?.recommendedCalories?.toString() || "";

    // Planning
    this.macronutrients.carbohydrates =
      mealPlan?.macronutrientsCarbohydratePercent?.toString() ?? "";
    this.macronutrients.proteins =
      mealPlan?.macronutrientsProteinPercent?.toString() ?? "";
    this.macronutrients.fats =
      mealPlan?.macronutrientsFatPercent?.toString() ?? "";
    this.foodExchange.use = mealPlan?.hasFoodExchange ?? false;
    this.foodExchange.values = {
      vegetables: mealPlan?.foodExchangeVegetables?.toString() ?? "",
      dairy: mealPlan?.foodExchangeDairy?.toString() ?? "",
      fatsAndOil: mealPlan?.foodExchangeFatsAndOil?.toString() ?? "",
      fruits: mealPlan?.foodExchangeFruits?.toString() ?? "",
      meatAndSeafood: mealPlan?.foodExchangeMeatAndSeafood?.toString() ?? "",
      plantBasedMeat: mealPlan?.foodExchangePlantBasedMeat?.toString() ?? "",
      starches: mealPlan?.foodExchangeStarches?.toString() ?? "",
      treats: mealPlan?.foodExchangeTreats?.toString() ?? "",
    };
  }

  private loadPlannedMeals(
    plannedMeals: FragmentType<typeof plannedMealFormFragment>[]
  ) {
    plannedMeals.forEach((pm) => {
      const plannedMeal = this.rows.add(
        pm.meal,
        parseTimeOfDay(pm.recommendedTime),
        pm.uuid
      );
      pm.mealPatternItems.forEach((mpi) => {
        const column = this.columns.at(Columns.MEAL_PATTERN_COLUMN_INDEX);
        const cell = this.cells.get(column, plannedMeal);
        cell.addMealPatternItem(
          mpi.name,
          mpi.amountServings,
          mpi.food,
          mpi.foodPortion,
          mpi.uuid
        );
      });
      pm.items.forEach((pmi) => {
        const dayColumn = this.columns.at(pmi.day);
        const cell = this.cells.get(dayColumn, plannedMeal);
        cell.addPlannedMealItem(
          pmi.food,
          pmi.foodPortion,
          pmi.amount,
          pmi.uuid
        );
      });
    });
  }
}

const plannedMealFormFragment = graphql(`
  fragment PlannedMealForm on PlannedMeal {
    id
    uuid
    meal
    recommendedTime
    items {
      id
      uuid
      day
      amount
      food {
        ...FoodItem
        id
        name
        category
        epWeightGrams
        energyKcal
      }
      foodPortion {
        id
        amount
        unitWithModifier
        weightGrams
      }
    }
    mealPatternItems {
      id
      uuid
      name
      amountServings
      food {
        ...FoodItem
        id
        name
        category
        epWeightGrams
        energyKcal
      }
      foodPortion {
        id
        amount
        unitWithModifier
        weightGrams
      }
    }
  }
`);

const programQuery = graphql(`
  query ProgramForMealPlanForm($id: ID!) {
    program(id: $id) {
      id
      name
      description
      startDate
      endDate
      mealPlan {
        id
        recommendedCalories
        hasMacronutrients
        macronutrientsCarbohydratePercent
        macronutrientsProteinPercent
        macronutrientsFatPercent
        hasFoodExchange
        foodExchangeVegetable
        foodExchangeFruit
        foodExchangeFat
        foodExchangeSugar
        foodExchangeMeatLowFat
        foodExchangeMeatModerateFat
        foodExchangeMeatHighFat
        foodExchangeMilkWhole
        foodExchangeMilkLowFat
        foodExchangeMilkNonFatSkim
        foodExchangeRiceLowProtein
        foodExchangeRiceMediumProtein
        foodExchangeRiceHighProtein
        foodExchangeVegetables
        foodExchangeTreats
        foodExchangeStarches
        foodExchangeDairy
        foodExchangeMeatAndSeafood
        foodExchangeFatsAndOil
        foodExchangeFruits
        foodExchangePlantBasedMeat
        plannedMeals {
          ...PlannedMealForm
        }
      }
    }
  }
`);

const mealPlanTemplateQuery = graphql(`
  query TemplateForMealPlanForm($id: ID!) {
    mealPlanTemplate(id: $id) {
      id
      name
      description
      durationDays
      recommendedCalories
      hasMacronutrients
      macronutrientsCarbohydratePercent
      macronutrientsProteinPercent
      macronutrientsFatPercent
      hasFoodExchange
      foodExchangeVegetable
      foodExchangeFruit
      foodExchangeFat
      foodExchangeSugar
      foodExchangeMeatLowFat
      foodExchangeMeatModerateFat
      foodExchangeMeatHighFat
      foodExchangeMilkWhole
      foodExchangeMilkLowFat
      foodExchangeMilkNonFatSkim
      foodExchangeRiceLowProtein
      foodExchangeRiceMediumProtein
      foodExchangeRiceHighProtein
      foodExchangeVegetables
      foodExchangeTreats
      foodExchangeStarches
      foodExchangeDairy
      foodExchangeMeatAndSeafood
      foodExchangeFatsAndOil
      foodExchangeFruits
      foodExchangePlantBasedMeat
      plannedMeals {
        ...PlannedMealForm
      }
    }
  }
`);

interface MealPlanLike {
  recommendedCalories: Scalars["Float"]["output"];
  macronutrientsCarbohydratePercent?: Maybe<Scalars["Float"]["output"]>;
  macronutrientsFatPercent?: Maybe<Scalars["Float"]["output"]>;
  macronutrientsProteinPercent?: Maybe<Scalars["Float"]["output"]>;
  hasFoodExchange: Scalars["Boolean"]["output"];
  foodExchangeVegetables?: Maybe<Scalars["Float"]["output"]>;
  foodExchangeTreats?: Maybe<Scalars["Float"]["output"]>;
  foodExchangeStarches?: Maybe<Scalars["Float"]["output"]>;
  foodExchangeDairy?: Maybe<Scalars["Float"]["output"]>;
  foodExchangeMeatAndSeafood?: Maybe<Scalars["Float"]["output"]>;
  foodExchangeFatsAndOil?: Maybe<Scalars["Float"]["output"]>;
  foodExchangeFruits?: Maybe<Scalars["Float"]["output"]>;
  foodExchangePlantBasedMeat?: Maybe<Scalars["Float"]["output"]>;
}
